/* eslint-disable @typescript-eslint/no-explicit-any */
import actorLayerIcon from '@/assets/images/icons/map/actor_icon.png'
import projectLayerIcon from '@/assets/images/icons/map/project_icon.png'
import resourceLayerIcon from '@/assets/images/icons/map/resource_icon.png'
import { ItemType } from '@/models/enums/app/ItemType'
import { Thematic } from '@/models/enums/contents/Thematic'
import type { ThematicItem } from '@/models/interfaces/common/ThematicItem'
import type { Layer } from '@/models/interfaces/map/Layer'
import { i18n } from '@/plugins/i18n'
import { useActorsStore } from '@/stores/actorsStore'
import { useApplicationStore } from '@/stores/applicationStore'
import { useMyMapStore } from '@/stores/myMapStore'
import { useProjectStore } from '@/stores/projectStore'
import { useResourceStore } from '@/stores/resourceStore'
import type { GeoJSONSource } from 'maplibre-gl'
import { computed } from 'vue'
import LayerService from './LayerService'
import MapService from './MapService'
import { OsmLayersService } from './osm/OsmLayersService'

/** Maximum zoom at which platform markers are clustered. */
const PLATFORM_CLUSTER_MAX_ZOOM = 14
/** Merge radius in pixels for platform marker clustering. */
const PLATFORM_CLUSTER_RADIUS_PX = 50
/** Cluster bubble colours per item type. */
const PLATFORM_CLUSTER_COLOR: Record<ItemType, string> = {
  [ItemType.PROJECT]: '#F0C94C',
  [ItemType.RESOURCE]: '#FDF6DA',
  [ItemType.ACTOR]: '#F0C94C',
}
/** Dark navy text — contrasts on all cluster bubble colours. */
const PLATFORM_CLUSTER_TEXT_COLOR = '#1B2942'

/** Filter restricting layers to non-cluster (individual) features. */
const UNCLUSTERED_FILTER = ['!', ['has', 'point_count']] as maplibregl.ExpressionSpecification

export class AppLayersService {
  private static mapThematics = Object.values(Thematic).map((t, i) => {
    return {
      id: i,
      name: t,
      isShown: false,
    }
  })
  static filteredProjects = computed(() => {
    const projectStore = useProjectStore()
    const myMapStore = useMyMapStore()
    const baseList =
      myMapStore.isProjectMapFiltersActive && myMapStore.projectMapFilteredList != null
        ? myMapStore.projectMapFilteredList
        : projectStore.projects
    return this.filterByThematic(baseList, myMapStore.projectSubLayers as Layer[])
  })
  static filteredActors = computed(() => {
    const actorStore = useActorsStore()
    const myMapStore = useMyMapStore()
    return this.filterByThematic(actorStore.actors, myMapStore?.actorSubLayers as Layer[])
  })
  static filteredResources = computed(() => {
    const resourceStore = useResourceStore()
    const myMapStore = useMyMapStore()
    const baseList =
      myMapStore.isResourceMapFiltersActive && myMapStore.resourceMapFilteredList != null
        ? myMapStore.resourceMapFilteredList
        : resourceStore.resources
    return this.filterByThematic(baseList, myMapStore.resourceSubLayers as Layer[])
  })

  static async initApplicationLayers(): Promise<void> {
    const projectStore = useProjectStore()
    const resourceStore = useResourceStore()
    const applicationStore = useApplicationStore()
    const myMapStore = useMyMapStore()
    const actorStore = useActorsStore()
    try {
      await Promise.all([
        resourceStore.getAll(myMapStore.activeCommuneCode),
        actorStore.getAll(),
        projectStore.getAll(myMapStore.activeCommuneCode),
      ])
      if (!myMapStore.isMapAlreadyBeenMounted) {
        this.initMainLayers()
        this.initSubLayers()
        await this.initOsmLayers()
      }

      if (myMapStore.map == null) return
      MapService.isLoaded(myMapStore.map, async () => {
        await this.setPlatformDataLayers()
        applicationStore.isLoading = false
      })
    } catch (error) {
      console.error("Erreur lors de l'initialisation des couches :", error)
      return Promise.reject(error)
    }
  }

  static initMainLayers() {
    const myMapStore = useMyMapStore()
    let showProject = false,
      showActor = false,
      showResource = false
    if (myMapStore?.deserializedMapState) {
      if (myMapStore.deserializedMapState?.layers?.projects) {
        showProject = true
      }
      if (myMapStore.deserializedMapState?.layers?.actors) {
        showActor = true
      }
      if (myMapStore.deserializedMapState?.layers?.resources) {
        showResource = true
      }
    }

    myMapStore!.projectLayer = LayerService.initLayer(
      {
        id: ItemType.PROJECT,
        name: i18n.t('myMap.rightSidebar.layers.itemType.' + ItemType.PROJECT),
        icon: projectLayerIcon,
      },
      showProject,
    )
    myMapStore!.actorLayer = LayerService.initLayer(
      {
        id: ItemType.ACTOR,
        name: i18n.t('myMap.rightSidebar.layers.itemType.' + ItemType.ACTOR),
        icon: actorLayerIcon,
      },
      showActor,
    )
    myMapStore!.resourceLayer = LayerService.initLayer(
      {
        id: ItemType.RESOURCE,
        name: i18n.t('myMap.rightSidebar.layers.itemType.' + ItemType.RESOURCE),
        icon: resourceLayerIcon,
      },
      showResource,
    )
  }

  static initSubLayers() {
    const myMapStore = useMyMapStore()
    const mapThematics = Object.values(Thematic).map((t, i) => {
      return {
        id: i,
        name: t,
        isShown: false,
      }
    })

    let projectThematics = this.mapThematics
    let actorsThematics = this.mapThematics
    let resourcesThematics = this.mapThematics

    if (myMapStore?.deserializedMapState) {
      projectThematics = mapThematics.map((x) => {
        return {
          ...x,
          isShown: myMapStore?.deserializedMapState?.layers?.projects?.includes(x.id) as boolean,
        }
      })

      actorsThematics = mapThematics.map((x) => {
        return {
          ...x,
          isShown: myMapStore?.deserializedMapState?.layers?.actors?.includes(x.id) as boolean,
        }
      })

      resourcesThematics = mapThematics.map((x) => {
        return {
          ...x,
          isShown: myMapStore?.deserializedMapState?.layers?.resources?.includes(x.id) as boolean,
        }
      })
    }
    myMapStore!.projectSubLayers = LayerService.initSubLayer(projectThematics as any)
    myMapStore!.actorSubLayers = LayerService.initSubLayer(actorsThematics as any)
    myMapStore!.resourceSubLayers = LayerService.initSubLayer(resourcesThematics)
  }

  static async setPlatformDataLayers() {
    const myMapStore = useMyMapStore()
    for (const itemType of Object.values(ItemType)) {
      const mainLayerShown =
        itemType === ItemType.PROJECT
          ? myMapStore.projectLayer?.isShown
          : itemType === ItemType.ACTOR
            ? myMapStore.actorLayer?.isShown
            : itemType === ItemType.RESOURCE
              ? myMapStore.resourceLayer?.isShown
              : false
      if (!mainLayerShown) continue
      await this.setPlatformDataLayer(itemType)
    }
    if (myMapStore!.isMapAlreadyBeenMounted) {
      myMapStore!.setMapLayersOrderOnMapReMount()
    }
    myMapStore!.isMapAlreadyBeenMounted = true
  }

  static async setPlatformDataLayer(itemType: ItemType) {
    const myMapStore = useMyMapStore()
    if (!myMapStore.myMap) return

    const geojson = this.getGeojsonPerItemType(itemType)
    const icon = new URL(`/src/assets/images/icons/map/${itemType}_icon.png`, import.meta.url).href

    // GeoJSON source with native MapLibre clustering + feature-state ID promotion.
    myMapStore.myMap.addSource(itemType, geojson, {
      promoteId: 'id',
      cluster: true,
      clusterMaxZoom: PLATFORM_CLUSTER_MAX_ZOOM,
      clusterRadius: PLATFORM_CLUSTER_RADIUS_PX,
    })

    try {
      await myMapStore.myMap.addImage(icon, itemType)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('already exists')) {
        console.debug(`Image "${itemType}" already exists, skipping.`)
      } else {
        console.error(`Error adding image "${itemType}":`, error)
      }
    }

    // Raw MapLibre map instance — needed for cluster layers and click handler.
    const rawMap = myMapStore.map as maplibregl.Map | null | undefined
    if (rawMap) {
      const clusterCirclesId = `${itemType}--cluster-circles`
      const clusterCountId = `${itemType}--cluster-count`

      // ── Cluster bubble ──────────────────────────────────────────────────
      if (!rawMap.getLayer(clusterCirclesId)) {
        rawMap.addLayer({
          id: clusterCirclesId,
          type: 'circle',
          source: itemType,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': PLATFORM_CLUSTER_COLOR[itemType],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,
              10,
              26,
              100,
              32,
            ],
            'circle-opacity': 0.9,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': 'rgba(255,255,255,0.8)',
          },
          metadata: { isPersistent: true, layerGroup: 'indicator' },
        })
      }

      // ── Cluster count label ──────────────────────────────────────────────
      if (!rawMap.getLayer(clusterCountId)) {
        rawMap.addLayer({
          id: clusterCountId,
          type: 'symbol',
          source: itemType,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': PLATFORM_CLUSTER_TEXT_COLOR },
          metadata: { isPersistent: true, layerGroup: 'indicator' },
        })
      }

      // ── Cluster click → zoom in ─────────────────────────────────────────
      rawMap.on('click', clusterCirclesId, async (e) => {
        const features = rawMap.queryRenderedFeatures(e.point, { layers: [clusterCirclesId] })
        const feature = features[0]
        if (!feature?.properties) return
        const clusterId = feature.properties.cluster_id as number
        const source = rawMap.getSource(itemType) as GeoJSONSource
        try {
          const zoom = await source.getClusterExpansionZoom(clusterId)
          rawMap.easeTo({
            center: (feature.geometry as GeoJSON.Point).coordinates as [number, number],
            zoom,
          })
        } catch {
          // Cluster already expanded or source not ready.
        }
      })
      rawMap.on('mouseenter', clusterCirclesId, () => {
        rawMap.getCanvas().style.cursor = 'pointer'
      })
      rawMap.on('mouseleave', clusterCirclesId, () => {
        rawMap.getCanvas().style.cursor = ''
      })
    }

    // ── Unclustered individual points (icon + hover/active highlight + hit area) ──
    const layout: maplibregl.LayerSpecification['layout'] = {
      'icon-image': itemType,
      'icon-size': 0.4,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    }
    myMapStore.myMap.addLayer(itemType, { layout, filter: UNCLUSTERED_FILTER })
    myMapStore.myMap.listenToHoveredFeature(itemType)
  }

  static getGeojsonPerItemType(itemType: ItemType) {
    switch (itemType) {
      case ItemType.ACTOR:
        return MapService.getGeojson(this.filteredActors.value)
      case ItemType.PROJECT:
        return MapService.getGeojson(this.filteredProjects.value)
      case ItemType.RESOURCE:
        return MapService.getGeojson(this.filteredResources.value)
    }
  }

  /**
   * GeoJSON pushed to the map for a platform layer: empty when the main layer is unchecked,
   * so markers and hit targets disappear and stay in sync with the sidebar.
   */
  static getGeojsonForMapDisplay(itemType: ItemType) {
    const myMapStore = useMyMapStore()
    const mainShown =
      itemType === ItemType.PROJECT
        ? (myMapStore.projectLayer?.isShown ?? false)
        : itemType === ItemType.ACTOR
          ? (myMapStore.actorLayer?.isShown ?? false)
          : itemType === ItemType.RESOURCE
            ? (myMapStore.resourceLayer?.isShown ?? false)
            : false
    if (!mainShown) {
      return MapService.getGeojson([])
    }
    return this.getGeojsonPerItemType(itemType)
  }

  static filterByThematic(items: ThematicItem[], subLayers: Layer[]) {
    const activeThematics: Layer['name'][] = subLayers
      .filter((layer: Layer) => layer.isShown)
      .map((layer: Layer) => layer.name)
    // No sub-layer checked: do not hide all items (default init has every isShown false).
    if (activeThematics.length === 0) {
      return items
    }
    return items.filter((item) => {
      const thematics = item.thematics ?? []
      // API may return no thematics; still show markers so the map is usable.
      if (thematics.length === 0) {
        return true
      }
      return activeThematics.some((thematic) => thematics.includes(thematic as Thematic))
    })
  }

  static async initOsmLayers(): Promise<void> {
    const myMapStore = useMyMapStore()
    try {
      const osmCategoryLayers = await OsmLayersService.initOsmLayers(myMapStore.activeCommuneCode)
      myMapStore.osmCategoryLayers = osmCategoryLayers
    } catch (error) {
      console.error("Erreur lors de l'initialisation des layers OSM:", error)
    }
  }
}
